import { LitElement, html, css } from "lit";
import { property, state } from "lit/decorators.js";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import "@maersk-global/mds-components-core/mc-radio";
import { _getFieldName, _shouldConvertUnit, convertMsToKt } from "./util";
import { ELEMENT_DATA, getIcingValueTexts, getVisibilityValueTexts } from "../heavy-weather-limits-model";

class VoyageHistoryView extends LitElement {
  @property({ type: Array }) _history: {
    changes_data: { name: string; from: string; to: string };
    createdAt: string;
    user_data: { name: string; email: string };
    imo?: string|number;
  }[] = [];
  @property({ type: Boolean }) customHistory = false
  @state() _currentPage = 1;
  @state() _itemsPerPage = 13;
  @state() _loading = true;
  @property({ attribute: false }) private _unit: string = "m/s";

  static get styles() {
    return css`
      .history-container {
        padding: 20px;
        width: 100%;
        height: 100vh;
        overflow-y: auto;
        box-sizing: border-box;
      }
      .table-container {
        width: 100%;
        margin: 0 auto;
        border: 1px solid var(--neutrals-grey-07);
        border-radius: 8px;
        background-color: var(--neutrals-grey-10);
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 0 auto;
        border: 1px solid var(--neutrals-grey-07);
        table-layout: fixed;
      }
      td {
        text-align: left;
        border-bottom: 1px solid var(--neutrals-grey-07);
        padding: 10px;
      }
      th {
        font-size: 18px;
        color: black;
        background: var(--neutrals-grey-09);
        text-align: left;
        padding: 8px;
        border-bottom: 1px solid var(--neutrals-grey-07);
      }
      .back-button {
        font-size: 16px;
        margin: 0 auto 20px auto;
        padding: 10px 20px;
        cursor: pointer;
        background-color: white;
        border: 1px solid var(--neutrals-grey-07);
        border-radius: 5px;
        width: 9%;
      }
      .pagination {
        display: flex;
        justify-content: center;
        margin: 20px 0;
      }
      .pagination button {
        margin: 0 5px;
        padding: 5px 10px;
        cursor: pointer;
        background-color: rgba(20, 20, 20, 1);
        color: var(--neutrals-grey-10, white);
        border: none;
        border-radius: 5px;
      }
      .radio-button-display {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding-bottom: 5px;
      }
      .radio-button-display p {
        margin: 0 10px 0 0;
      }
      .radio-button-display input[type="radio"] {
        margin: 0 5px;
      }
      .no-data {
        margin: 20px;
        text-align: center;
      }
        .timestamp-column {
        width: 17%;
      }
      .modified-by-column {
        width: 12%;
      }
      .from-column {
        width: 18%;
      }
      .to-column {
        width: 18%;
      }
      .padding{
        padding-right: 10px;
      }
    `;
  }

  async connectedCallback() {
    super.connectedCallback();
    this._loading = true;
    let historyData = await VoyFleetAlarmModel!.getCompanyWeatherLimitsHistory(this.customHistory);
    this._history = historyData;
    this._loading = false;
  }

  private _handleUnitChange(e: Event) {
    const value = (e.target as HTMLInputElement).id;
    if (this._unit == value) return;
    this._unit = value;

    this.requestUpdate();
  }

  private formatCustomEntryValues = (entry: any, type:string): string => {
    const elementName = entry?.changes_data?.name;
    const value = type =="to" ? entry?.changes_data?.to :  entry?.changes_data?.from;
    const isConvertible = _shouldConvertUnit(elementName);

    if (value == null || isNaN(value)) return "--";

    if(elementName.toLowerCase() == "visibility") {
      const visibilityArray = getVisibilityValueTexts();
      const found = visibilityArray.find((item) => item.value === value);
      return found ? found.text : "--";
    }

    else if( elementName.toLowerCase()=== "icing") {
      const icingArray = getIcingValueTexts();
      const found = icingArray.find((item) => item.value === value);
      return found ? found.text : "--";
    }

  else{
      if (isConvertible) {
        const convertedValue = this._unit === 'kt'
          ? convertMsToKt(Number(value)).toFixed(2)
          : Number(value).toFixed(2);
        
        return `${convertedValue} ${this._unit}`;
      }

      // fallback to unit from ELEMENT_DATA if not convertible
      const element = ELEMENT_DATA.find(item => elementName.includes(item.name));
      const defaultUnit = element?.unit || '';

      return `${Number(value).toFixed(2)} ${defaultUnit}`;
    } 
};

  render() {

    const formatEntryData = (entry: any) => {
      const elementNameWithPeriods = "with periods";
      const elementNameMaxWave = "wave";
      const elementUnitKt = "(kt)";
      const elementUnitMs = "(m/s)";
      let unitFrom = entry.changes_data.from;
      let unitTo = entry.changes_data.to;
      let unit = '';

        if (unitFrom != null || unitTo != null) { 
          if (entry.changes_data.name?.toLowerCase().includes(elementNameWithPeriods)) {
          unit = 's';
          if (unitFrom !== null && unitFrom !== undefined) {
            unitFrom = `${unitFrom} ${unit}`;
          }
          if (unitTo !== null && unitTo !== undefined) {
            unitTo = `${unitTo} ${unit}`;
          }
        } else if (entry.changes_data.name?.toLowerCase()?.includes(elementNameMaxWave)) {
          unit = 'm';
          if (unitFrom !== null && unitFrom !== undefined) {
            unitFrom = `${unitFrom} ${unit}`;
          }
          if (unitTo !== null && unitTo !== undefined) {
            unitTo = `${unitTo} ${unit}`;
          }
        } else if (entry.changes_data.name?.toLowerCase().includes(elementUnitKt)) {
          const selectedUnit = this._unit;
          if (selectedUnit === 'm/s') {
            unit = 'm/s';
            if (unitFrom !== null && unitFrom !== undefined) {
              unitFrom = entry.changes_data.from / 1.94384;
              unitFrom = `${unitFrom.toFixed(2)} ${unit}`;
            }
            if (unitTo !== null && unitTo !== undefined) {
              unitTo = entry.changes_data.to / 1.94384;
              unitTo = `${unitTo.toFixed(2)} ${unit}`;
            }
          } else if (selectedUnit === 'kt') {
            unit = 'kt';
            if (unitFrom !== null && unitFrom !== undefined) {
              unitFrom = entry.changes_data.from;
              unitFrom = `${unitFrom} ${unit}`;
            }
            if (unitTo !== null && unitTo !== undefined) {
              unitTo = entry.changes_data.to;
              unitTo = `${unitTo} ${unit}`;
            }
          }
        } else if (entry.changes_data.name?.toLowerCase().includes(elementUnitMs)) {
          const selectedUnit = this._unit;
          if(selectedUnit === 'm/s'){
            unit = 'm/s';
            if (unitFrom !== null && unitFrom !== undefined) {
              unitFrom = entry.changes_data.from;
              unitFrom = `${unitFrom} ${unit}`;
            }
            if (unitTo !== null && unitTo !== undefined) {
              unitTo = entry.changes_data.to;
              unitTo = `${unitTo} ${unit}`;
            }
          } else if (selectedUnit === 'kt') {
            unit = 'kt';
            if (unitFrom !== null && unitFrom !== undefined) {
              unitFrom = entry.changes_data.from * 1.94384;
              unitFrom = `${unitFrom.toFixed(1)} ${unit}`;
            }
            if (unitTo !== null && unitTo !== undefined) {
              unitTo = entry.changes_data.to * 1.94384;
              unitTo = `${unitTo.toFixed(1)} ${unit}`;
            }
          }
        }
      }
      return { unitFrom: unitFrom || '', unitTo: unitTo || '' };
      
    };

    if ((!this._loading && (this._history?.length ?? 0)) === 0)
      return html`<div>
        <div class="no-data">No Data</div>
      </div>`;
    const totalItems = this._history.length ?? 0;
    const totalPages = Math.ceil(totalItems / this._itemsPerPage);
    const startIdx = (this._currentPage - 1) * this._itemsPerPage;
    const endIdx = startIdx + this._itemsPerPage;
    const paginatedHistory = this._history.slice(startIdx, endIdx);

    return html`
      <div id="container" class="history-container">
        ${this._loading
          ? html`<div class="spinner">
              <mc-loading-indicator
                fit="large"
                label="Loading data..."
              ></mc-loading-indicator>
            </div>`
          : html`
              <div class="radio-button-display">
                <div class="padding">Change wind unit:</div>
                <div class="padding">
                  <mc-radio
                    id="m/s"
                    name="windUnit"
                    label="m/s"
                    @change=${(evt:InputEvent) => this._handleUnitChange(evt)}
                    ?checked=${this._unit == "m/s"}
                  />
                </div>
                <div class="padding">
                <mc-radio
                    id="kt"
                    name="windUnit"
                    label="kt"
                    @change=${(evt:InputEvent) => this._handleUnitChange(evt)}
                    ?checked=${this._unit == "kt"}
                  />
                </div>
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th class="timestamp-column">Timestamp</th>
                       ${this.customHistory ? html`<th class="">Imo</th>` : ''}
                      <th class="modified-by-column">Modified by</th>
                      <th class="field-modified-column">Field modified</th>
                      <th class="from-column">From</th>
                      <!-- <th class="to-column">To</th> -->
                       <th class="">To</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${paginatedHistory.map((entry) => {
                      const date = new Date(entry.createdAt);
                      const formattedDate = date.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      });
                      const formattedTime = date.toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: false,
                      });
                      const formattedDateTime = `${formattedDate} ${formattedTime}`;
                      return html`
                        <tr>
                          <td>${formattedDateTime}</td>
                           ${this.customHistory ? html`<td>${entry.imo}</td>` : ''}
                          <td>${entry.user_data.name}</td>
                          <td>${this.customHistory ? _getFieldName(entry.changes_data.name):entry.changes_data.name }</td>
                            <td>
                              ${this.customHistory
                                ? ((entry.changes_data.from !== null && entry.changes_data.from !== undefined)
                                    ? ` ${this.formatCustomEntryValues(entry,"from")}`
                                    : "--")
                                : formatEntryData(entry).unitFrom}
                            </td>
 <td>
                              ${this.customHistory
                                ? ((entry.changes_data.to !== null && entry.changes_data.to !== undefined)
                                    ? ` ${this.formatCustomEntryValues(entry,"to")}`
                                    : "--")
                                : formatEntryData(entry).unitTo}
                            </td>                          
                        </tr>
                      `;
                    })}
                  </tbody>
                </table>
              </div>
              <div class="pagination">
                <button
                  @click="${this._prevPage}"
                  ?disabled="${this._currentPage === 1}"
                >
                  Previous
                </button>
                <span>Page ${this._currentPage} of ${totalPages}</span>
                <button
                  @click="${this._nextPage}"
                  ?disabled="${this._currentPage === totalPages}"
                >
                  Next
                </button>
              </div>
            `}
      </div>
    `;
  }

  _prevPage() {
    if (this._currentPage > 1) {
      this._currentPage--;
    }
  }

  _nextPage() {
    if (
      this._currentPage < Math.ceil(this._history.length / this._itemsPerPage)
    ) {
      this._currentPage++;
    }
  }

  _onBack() {
    this.dispatchEvent(new CustomEvent("back"));
  }
}

customElements.define("voyage-view-history", VoyageHistoryView);
